"use client";

import React, { useEffect, useRef, useState } from "react";
import { BrainCircuit, CloudCog, ShieldCheck, Bot, Sparkles, Cpu } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2018",
  title: "Facility Launch",
  description: "Established our FDA-registered facility with a primary focus on high-precision carbonation and initial small-batch canning lines.",
  iconName: "Cpu"
}, {
  year: "2019",
  title: "Nutraceutical Focus",
  description: "Pioneered specialized formulation processes for probiotic fizzes and adaptogen-infused seltzers for the wellness market.",
  iconName: "Sparkles"
}, {
  year: "2021",
  title: "Capacity Expansion",
  description: "Upgraded to high-speed counter-pressure canning technology, doubling our throughput for enterprise-level contract orders.",
  iconName: "CloudCog"
}, {
  year: "2022",
  title: "Compliance Mastery",
  description: "Achieved elite GMP certifications and implemented rigorous zero-defect quality control protocols across all production stages.",
  iconName: "ShieldCheck"
}, {
  year: "2023",
  title: "R&D Lab Innovation",
  description: "Launched our dedicated custom formulation lab, enabling low-MOQ pilot runs for emerging functional beverage startups.",
  iconName: "BrainCircuit"
}, {
  year: "2024",
  title: "Sustainable Logistics",
  description: "Integrated 100% recyclable aluminum sourcing and energy-efficient manufacturing to minimize the carbon footprint of our cans.",
  iconName: "Bot"
}];
const iconMap: Record<string, React.ElementType> = {
  BrainCircuit,
  CloudCog,
  ShieldCheck,
  Bot,
  Sparkles,
  Cpu
};
function TimelineItem({
  milestone,
  index
}: {
  milestone: typeof milestones[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  const Icon = iconMap[milestone.iconName] || BrainCircuit;
  return <div data-section-id="139433" ref={ref} data-index={index} className={`relative flex flex-col md:flex-row items-center justify-between w-full mb-12 md:mb-24 last:mb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute left-[1.375rem] md:left-1/2 top-8 md:top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-10 h-10 rounded-full border-2 border-primary bg-background shadow-[0_0_15px_rgba(var(--color-primary),0.5)] transition-transform duration-500 hover:scale-110">
        <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
      </div>

      {}
      <div className={`w-full pl-16 md:pl-0 md:w-[45%] flex ${isEven ? "md:pr-12 md:justify-end" : "md:ml-auto md:pl-12 md:justify-start"}`}>
        <Card className="w-full max-w-md bg-card/40 backdrop-blur-xl border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(var(--color-primary),0.15)] group relative overflow-hidden">
          {}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <CardHeader className="relative z-10 pb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl font-bold text-primary/90 font-mono tracking-tighter">
                {milestone.year}
              </span>
              <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                <Icon className="w-6 h-6" />
              </div>
            </div>
            <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors duration-300 font-sans tracking-tight">
              {milestone.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10">
            <CardDescription className="text-muted-foreground text-base leading-relaxed font-sans">
              {milestone.description}
            </CardDescription>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function OurTechnologyRoadmap() {
  return <section id="quality-milestones" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.05] pointer-events-none bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      {}
      <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none -translate-x-1/2" />
      <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-sm font-bold tracking-widest text-primary uppercase mb-4 font-mono">
            Evolution of Precision
          </h2>
          <h3 className="text-4xl md:text-5xl font-extrabold text-foreground mb-6 tracking-tighter font-sans">
            Our Quality Milestones
          </h3>
          <p className="text-lg text-muted-foreground font-sans">
            Tracing the strategic journey of Custom RTD Co. as we define the gold standard in functional beverage contract manufacturing.
          </p>
        </div>

        <div className="relative">
          {}
          <div className="absolute left-[1.375rem] md:left-1/2 top-4 bottom-4 w-0.5 bg-gradient-to-b from-transparent via-primary/50 to-transparent -translate-x-1/2 shadow-[0_0_15px_rgba(var(--color-primary),0.3)]" />

          <div className="relative z-10 flex flex-col">
            {milestones.map((milestone, index) => <TimelineItem key={milestone.year} milestone={milestone} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}